// API Endpoints para integração com backend de produção

const API_BASE_URL = 'https://api.cotacaoai.com.br/api'

// Configuração base para requisições
const getApiConfig = () => {
  const token = localStorage.getItem('authToken')
  return {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
  }
}

// Utility function para fazer requisições
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`
  const config = {
    ...getApiConfig(),
    ...options,
    headers: {
      ...getApiConfig().headers,
      ...(options.headers || {})
    }
  }

  try {
    console.log('=== API REQUEST DEBUG ===')
    console.log('API_BASE_URL:', API_BASE_URL)
    console.log('Endpoint:', endpoint)
    console.log('Final URL:', url)
    console.log('Config:', config)
    console.log('========================')
    const response = await fetch(url, config)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error Response:', errorData)
      console.error('Request Body:', config.body)
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  } catch (error) {
    console.error('API Request failed:', error)
    console.error('URL:', url)
    console.error('Config:', config)
    
    // Se for erro de rede, dar uma mensagem mais clara
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error('Não foi possível conectar ao servidor. Verifique se o backend está rodando em ' + API_BASE_URL)
    }
    
    throw error
  }
}

// ===== AUTHENTICATION ENDPOINTS =====
export const authAPI = {
  // Admin login
  login: (credentials) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }),
  // Client login
  clientLogin: (credentials) => apiRequest('/auth/client-login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }),
  // Register new user
  register: (userData) => apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData)
  }),
  logout: () => apiRequest('/auth/logout', {
    method: 'POST'
  }),
  verify: () => apiRequest('/auth/verify'),
  refresh: () => apiRequest('/auth/refresh', {
    method: 'POST'
  }),
  resetPassword: (email) => apiRequest('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ email })
  }),
  confirmReset: (data) => apiRequest('/auth/confirm-reset', {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

// ===== PLANS ENDPOINTS =====
export const plansAPI = {
  getAll: () => apiRequest('/status/plans'),
  getById: (id) => apiRequest(`/status/plans/${id}`)
}

// ===== STATUS ENDPOINTS =====
export const statusAPI = {
  // Quotation statuses
  getQuotationStatuses: () => apiRequest('/api/status/quotation-statuses'),
  // Supplier statuses  
  getSupplierStatuses: () => apiRequest('/api/status/supplier-statuses'),
  // Plans
  getPlans: () => apiRequest('/api/status/plans'),
  getPlanById: (id) => apiRequest(`/api/status/plans/${id}`)
}

// ===== CLIENTS ENDPOINTS =====
export const clientsAPI = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString()
    return apiRequest(`/clients${queryString ? `?${queryString}` : ''}`)
  },
  search: (query, params = {}) => {
    const queryString = new URLSearchParams({ q: query, ...params }).toString()
    return apiRequest(`/clients/search?${queryString}`)
  },
  getById: (id) => apiRequest(`/clients/${id}`),
  create: (data) => apiRequest('/clients', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/clients/${id}`, {
    method: 'DELETE'
  })
}

// ===== CLIENT ADDRESSES ENDPOINTS =====
export const clientAddressesAPI = {
  getByClientId: (clientId) => apiRequest(`/clients/${clientId}/addresses`),
  getById: (id) => apiRequest(`/clients/addresses/${id}`),
  create: (clientId, data) => apiRequest(`/clients/${clientId}/addresses`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/clients/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/clients/addresses/${id}`, {
    method: 'DELETE'
  }),
  setPrimary: (id) => apiRequest(`/clients/addresses/${id}/primary`, {
    method: 'PATCH'
  })
}


// ===== QUOTATIONS ENDPOINTS =====
export const quotationsAPI = {
  getAll: () => apiRequest('/quotations'),
  getById: (id) => apiRequest(`/quotations/${id}`),
  getItems: (id) => apiRequest(`/quotations/${id}/items`),
  getSuppliers: (id) => apiRequest(`/quotations/${id}/suppliers`),
  getResponses: (quotationId) => apiRequest(`/quotations/${quotationId}/responses`),
  create: (data) => apiRequest('/quotations', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/quotations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/quotations/${id}`, {
    method: 'DELETE'
  }),
  expire: (id) => apiRequest(`/quotations/${id}/expire`, {
    method: 'PATCH'
  }),
  close: (id) => apiRequest(`/quotations/${id}/close`, {
    method: 'PATCH'
  }),
  // Supplier responses
  getSupplierResponseById: (id) => apiRequest(`/quotations/supplier-responses/${id}`),
  updateSupplierResponse: (id, data) => apiRequest(`/quotations/supplier-responses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteSupplierResponse: (id) => apiRequest(`/quotations/supplier-responses/${id}`, {
    method: 'DELETE'
  })
}

// ===== ITEMS ENDPOINTS =====
export const itemsAPI = {
  // Public endpoint
  search: (searchTerm) => apiRequest(`/api/items/search?q=${encodeURIComponent(searchTerm)}`),
  // Protected endpoints
  getAll: () => apiRequest('/items'),
  getById: (id) => apiRequest(`/items/${id}`),
  create: (data) => apiRequest('/items', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/items/${id}`, {
    method: 'DELETE'
  })
}

// ===== QUOTATION ITEMS ENDPOINTS =====
export const quotationItemsAPI = {
  getByQuotationId: (quotationId) => apiRequest(`/quotations/${quotationId}/items`),
  getById: (id) => apiRequest(`/quotation-items/${id}`),
  create: (data) => apiRequest('/quotation-items', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/quotation-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/quotation-items/${id}`, {
    method: 'DELETE'
  })
}


// ===== SUPPLIERS ENDPOINTS =====
export const suppliersAPI = {
  // Public endpoints
  search: (searchTerm) => apiRequest(`/suppliers/search?q=${encodeURIComponent(searchTerm)}`),
  // Protected endpoints
  getAll: () => apiRequest('/suppliers/my-suppliers'),
  getById: (id) => apiRequest(`/suppliers/${id}`),
  create: (data) => apiRequest('/suppliers', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/suppliers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/suppliers/${id}`, {
    method: 'DELETE'
  })
}

// ===== QUOTATION SUPPLIERS ENDPOINTS =====
export const quotationSuppliersAPI = {
  // Public endpoint
  getByToken: (linkToken) => apiRequest(`/quotation-suppliers/token/${linkToken}`),
  // Submit supplier response (public)
  submitResponse: (data) => apiRequest('/quotations/supplier-responses', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  // Protected endpoints
  getByQuotationId: (quotationId) => apiRequest(`/quotations/${quotationId}/suppliers`),
  getById: (id) => apiRequest(`/quotation-suppliers/${id}`),
  create: (data) => apiRequest('/quotation-suppliers', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/quotation-suppliers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/quotation-suppliers/${id}`, {
    method: 'DELETE'
  })
}

// ===== SUPPLIER RESPONSES ENDPOINTS =====
export const supplierResponsesAPI = {
  getByQuotationId: (quotationId) => apiRequest(`/quotations/${quotationId}/responses`),
  getById: (id) => apiRequest(`/quotations/supplier-responses/${id}`),
  create: (data) => apiRequest('/quotations/supplier-responses/supplier-responses', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/quotations/supplier-responses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/quotations/supplier-responses/${id}`, {
    method: 'DELETE'
  })
}

// ===== UNITS OF MEASURE ENDPOINTS =====
export const unitsOfMeasureAPI = {
  // Public endpoint
  search: (searchTerm) => apiRequest(`/units-of-measure/search?q=${encodeURIComponent(searchTerm)}`),
  // Public endpoint for getting all units
  getAll: () => apiRequest('/units/all'),
  // Protected endpoints
  getById: (id) => apiRequest(`/units-of-measure/${id}`),
  create: (data) => apiRequest('/units-of-measure', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/units-of-measure/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/units-of-measure/${id}`, {
    method: 'DELETE'
  })
}

// ===== SUPPLIER RESPONSE ITEMS ENDPOINTS =====
export const supplierResponseItemsAPI = {
  getBySupplierResponseId: (supplierResponseId) => apiRequest(`/supplier-responses/${supplierResponseId}/items`),
  getById: (id) => apiRequest(`/supplier-response-items/${id}`),
  create: (data) => apiRequest('/supplier-response-items', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => apiRequest(`/supplier-response-items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => apiRequest(`/supplier-response-items/${id}`, {
    method: 'DELETE'
  })
}

// ===== ANALYTICS ENDPOINTS =====
export const analyticsAPI = {
  getDashboard: () => apiRequest('/analytics/dashboard'),
  getQuotations: () => apiRequest('/analytics/quotations'),
  getSuppliers: () => apiRequest('/analytics/suppliers'),
  getPerformance: () => apiRequest('/analytics/performance')
}

// ===== COMPOSITE ENDPOINTS =====
// Endpoints que combinam múltiplas entidades para facilitar o uso
export const compositeAPI = {
  // Buscar cotação por token com todos os dados necessários para o fornecedor
  getQuotationByToken: async (linkToken) => {
    return await quotationSuppliersAPI.getByToken(linkToken)
  },
  
  // Submeter resposta completa do fornecedor
  submitSupplierResponse: async (responseData) => {
    return await supplierResponsesAPI.create(responseData)
  }
}

export default {
  auth: authAPI,
  status: statusAPI,
  clients: clientsAPI,
  clientAddresses: clientAddressesAPI,
  quotations: quotationsAPI,
  items: itemsAPI,
  quotationItems: quotationItemsAPI,
  suppliers: suppliersAPI,
  quotationSuppliers: quotationSuppliersAPI,
  supplierResponses: supplierResponsesAPI,
  unitsOfMeasure: unitsOfMeasureAPI,
  supplierResponseItems: supplierResponseItemsAPI,
  analytics: analyticsAPI,
  composite: compositeAPI
}